import { UNGROUPED_RESOURCES_GROUP_ID } from '@/console/features/groups/components/group-list/group-list.constants';
import {
  useReadGroupedAssetsCountQuery,
  useReadUngroupedAssetsCountQuery,
} from '@/shared/redux/api/assets/groups/groups-api';
import {
  useReadGroupedIdentitiesCountQuery,
  useReadUngroupedIdentitiesCountQuery,
} from '@/shared/redux/api/ident/ident-api';
import {
  useReadGroupedIntegrationsCountQuery,
  useReadUngroupedIntegrationsCountQuery,
} from '@/shared/redux/api/integration/integrations-api';
import {
  useReadGroupedPipelinesCountQuery,
  useReadUngroupedPipelinesCountQuery,
} from '@/shared/redux/api/jobs/jobs-api';

export const useGroupResourcesCount = (groupId?: string) => {
  const isUngrouped = groupId === UNGROUPED_RESOURCES_GROUP_ID;

  const {
    data: groupedAssetsData,
    isLoading: groupedAssetsLoading,
    isFetching: groupedAssetsFetching,
  } = useReadGroupedAssetsCountQuery(
    { groupId: groupId! },
    { skip: !groupId || isUngrouped },
  );

  const {
    data: groupedIdentitiesData,
    isLoading: groupedIdentitiesLoading,
    isFetching: groupedIdentitiesFetching,
  } = useReadGroupedIdentitiesCountQuery(
    { groupId: groupId! },
    { skip: !groupId || isUngrouped },
  );

  const {
    data: groupedPipelinesData,
    isLoading: groupedPipelinesLoading,
    isFetching: groupedPipelinesFetching,
  } = useReadGroupedPipelinesCountQuery(
    { groupId: groupId! },
    { skip: !groupId || isUngrouped },
  );

  const {
    data: groupedIntegrationsData,
    isLoading: groupedIntegrationsLoading,
    isFetching: groupedIntegrationsFetching,
  } = useReadGroupedIntegrationsCountQuery(
    { groupId: groupId! },
    { skip: !groupId || isUngrouped },
  );

  const groupedLoading =
    groupedAssetsLoading ||
    groupedIdentitiesLoading ||
    groupedPipelinesLoading ||
    groupedIntegrationsLoading;

  const groupedFetching =
    groupedAssetsFetching ||
    groupedIdentitiesFetching ||
    groupedPipelinesFetching ||
    groupedIntegrationsFetching;

  const groupedSubGroupsCount =
    groupedAssetsData?.readSubGroupsForResourceGroup.itemCount || 0;
  const groupedDevicesCount = groupedAssetsData?.readAllDevices.itemCount || 0;
  const groupedProjectsCount =
    groupedAssetsData?.readAllProjects.itemCount || 0;
  const groupedGatewaysCount =
    groupedAssetsData?.readAllGateways.itemCount || 0;
  const groupedDocumentsCount =
    groupedAssetsData?.readAllDocuments.itemCount || 0;
  const groupedVaultsCount = groupedAssetsData?.readAllVaults.itemCount || 0;
  const groupedLicensesCount =
    groupedAssetsData?.readAllLicenses.itemCount || 0;
  const groupedPipelinesCount =
    groupedPipelinesData?.readAllPipelineConfigurations.itemCount || 0;
  const groupedUsersCount = groupedIdentitiesData?.readAllUsers.itemCount || 0;
  const groupedRolesCount =
    groupedIdentitiesData?.readAllUserRoles.itemCount || 0;
  const groupedIntegrationsCount =
    groupedIntegrationsData?.readAllIntegrations.itemCount || 0;

  const groupedAllResourcesCount =
    groupedSubGroupsCount +
    groupedDevicesCount +
    groupedProjectsCount +
    groupedGatewaysCount +
    groupedDocumentsCount +
    groupedPipelinesCount +
    groupedVaultsCount +
    groupedUsersCount +
    groupedRolesCount +
    groupedLicensesCount +
    groupedIntegrationsCount;

  const {
    data: ungroupedAssetsData,
    isLoading: ungroupedAssetsLoading,
    isFetching: ungroupedAssetsFetching,
  } = useReadUngroupedAssetsCountQuery(undefined, {
    skip: !groupId || !isUngrouped,
  });

  const {
    data: ungroupedIdentitiesData,
    isLoading: ungroupedIdentitiesLoading,
    isFetching: ungroupedIdentitiesFetching,
  } = useReadUngroupedIdentitiesCountQuery(undefined, {
    skip: !groupId || !isUngrouped,
  });

  const {
    data: ungroupedPipelinesData,
    isLoading: ungroupedPipelinesLoading,
    isFetching: ungroupedPipelinesFetching,
  } = useReadUngroupedPipelinesCountQuery(undefined, {
    skip: !groupId || !isUngrouped,
  });

  const {
    data: ungroupedIntegrationsData,
    isLoading: ungroupedIntegrationsLoading,
    isFetching: ungroupedIntegrationsFetching,
  } = useReadUngroupedIntegrationsCountQuery(undefined, {
    skip: !groupId || !isUngrouped,
  });

  const ungroupedLoading =
    ungroupedAssetsLoading ||
    ungroupedIdentitiesLoading ||
    ungroupedPipelinesLoading ||
    ungroupedIntegrationsLoading;

  const ungroupedFetching =
    ungroupedAssetsFetching ||
    ungroupedIdentitiesFetching ||
    ungroupedPipelinesFetching ||
    ungroupedIntegrationsFetching;

  const ungroupedDevicesCount =
    ungroupedAssetsData?.readAllDevices.itemCount || 0;
  const ungroupedProjectsCount =
    ungroupedAssetsData?.readAllProjects.itemCount || 0;
  const ungroupedGatewaysCount =
    ungroupedAssetsData?.readAllGateways.itemCount || 0;
  const ungroupedDocumentsCount =
    ungroupedAssetsData?.readAllDocuments.itemCount || 0;
  const ungroupedVaultsCount =
    ungroupedAssetsData?.readAllVaults.itemCount || 0;
  const ungroupedLicensesCount =
    ungroupedAssetsData?.readAllLicenses.itemCount || 0;
  const ungroupedPipelinesCount =
    ungroupedPipelinesData?.readAllPipelineConfigurations.itemCount || 0;
  const ungroupedUsersCount =
    ungroupedIdentitiesData?.readAllUsers.itemCount || 0;
  const ungroupedRolesCount =
    ungroupedIdentitiesData?.readAllUserRoles.itemCount || 0;
  const ungroupedIntegrationsCount =
    ungroupedIntegrationsData?.readAllIntegrations.itemCount || 0;

  const ungroupedAllResourcesCount =
    ungroupedDevicesCount +
    ungroupedProjectsCount +
    ungroupedGatewaysCount +
    ungroupedDocumentsCount +
    ungroupedPipelinesCount +
    ungroupedVaultsCount +
    ungroupedUsersCount +
    ungroupedRolesCount +
    ungroupedLicensesCount +
    ungroupedIntegrationsCount;

  return {
    isLoading: isUngrouped ? ungroupedLoading : groupedLoading,
    isFetching: isUngrouped ? ungroupedFetching : groupedFetching,
    subGroupsCount: isUngrouped ? 0 : groupedSubGroupsCount,
    devicesCount: isUngrouped ? ungroupedDevicesCount : groupedDevicesCount,
    projectsCount: isUngrouped ? ungroupedProjectsCount : groupedProjectsCount,
    gatewaysCount: isUngrouped ? ungroupedGatewaysCount : groupedGatewaysCount,
    documentsCount: isUngrouped
      ? ungroupedDocumentsCount
      : groupedDocumentsCount,
    pipelinesCount: isUngrouped
      ? ungroupedPipelinesCount
      : groupedPipelinesCount,
    vaultsCount: isUngrouped ? ungroupedVaultsCount : groupedVaultsCount,
    usersCount: isUngrouped ? ungroupedUsersCount : groupedUsersCount,
    rolesCount: isUngrouped ? ungroupedRolesCount : groupedRolesCount,
    licensesCount: isUngrouped ? ungroupedLicensesCount : groupedLicensesCount,
    integrationsCount: isUngrouped
      ? ungroupedIntegrationsCount
      : groupedIntegrationsCount,
    allResourcesCount: isUngrouped
      ? ungroupedAllResourcesCount
      : groupedAllResourcesCount,
  };
};
